import React, { useMemo } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import SignUp from './pages/SignUp';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import { Box, ThemeProvider, CssBaseline } from '@mui/material';
import { useSelector } from 'react-redux';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { getTheme } from './theme';
import Layout from './components/Layout';
import AuthRedirectHandler from './components/AuthRedirectHandler';
import Dashboard from './pages/Dashboard';
import CompetitionCreate from './pages/CompetitionCreate';
import CompetitionDetails from './pages/CompetitionDetails';
import AthleteOrganizer from './pages/AthleteOrganizer';
import AdminDashboard from './pages/AdminDashboard';
import CompetitionProgressDemo from './components/CompetitionProgress/Demo';

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) return null;
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  return children;
};

// Component to handle auth redirects
function AppWithRedirectHandler() {
  return (
    <>
      <AuthRedirectHandler />
      <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/competition/create" element={
            <ProtectedRoute>
              <CompetitionCreate />
            </ProtectedRoute>
          } />
          {/* Redirect /competition/:id to /competition/:id/schedule by default */}
          <Route path="/competition/:id" element={<Navigate to="schedule" replace />} />

          {/* Competition tab routes */}
          <Route path="/competition/:id/schedule" element={
            <ProtectedRoute>
              <CompetitionDetails />
            </ProtectedRoute>
          } />
          <Route path="/competition/:id/sessions" element={
            <ProtectedRoute>
              <CompetitionDetails />
            </ProtectedRoute>
          } />
          <Route path="/competition/:id/athletes" element={
            <ProtectedRoute>
              <CompetitionDetails />
            </ProtectedRoute>
          } />
          <Route path="/competition/:id/judges" element={
            <ProtectedRoute>
              <CompetitionDetails />
            </ProtectedRoute>
          } />
          <Route path="/competition/:id/statistics" element={
            <ProtectedRoute>
              <CompetitionDetails />
            </ProtectedRoute>
          } />
          <Route path="/competition/:id/venue" element={
            <ProtectedRoute>
              <CompetitionDetails />
            </ProtectedRoute>
          } />
          <Route path="/competition/:id/results" element={
            <ProtectedRoute>
              <CompetitionDetails />
            </ProtectedRoute>
          } />
          <Route path="/competition/:id/organize" element={
            <ProtectedRoute>
              <AthleteOrganizer />
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          {/* Temporary demo route - remove after testing */}
          <Route path="/demo/progress" element={<CompetitionProgressDemo />} />
        </Routes>
    </>
  );
}

function App() {
  const mode = useSelector((state) => state.theme.mode);
  const theme = useMemo(() => getTheme(mode), [mode]);
  
  return (
    <AuthProvider>
      <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <CssBaseline enableColorScheme />
        <Layout>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <AppWithRedirectHandler />  
      </Box>
        </Layout>
      </LocalizationProvider>
    </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
